import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Globe, Server, DollarSign, ArrowRight, Sparkles } from 'lucide-react';
import { validateEmail, normalizeEmail } from '../utils/emailValidation';

const API = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = [
  {
    id: 'website_setup',
    headline: '$500 OFF Website Setup',
    subheadline: 'Launch your dream website for less',
    description: 'Get a professional, modern website built for your business with $500 off our standard setup fee. Limited time offer.',
    cta: 'Claim My $500 Discount',
    redirect: '/services',
    icon: Globe,
    gradient: 'from-blue-600 to-blue-700',
    bgGlow: 'bg-blue-500/20',
    accent: 'text-blue-400',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    id: 'free_hosting',
    headline: 'First Month Hosting FREE',
    subheadline: 'Up to $299 value — on us',
    description: 'Experience blazing-fast, fully managed hosting for your business website. First month completely free, no strings attached.',
    cta: 'Start Free Hosting',
    redirect: '/services',
    icon: Server,
    gradient: 'from-blue-700 to-cyan-700',
    bgGlow: 'bg-blue-500/20',
    accent: 'text-cyan-400',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    id: 'affiliate',
    headline: 'Become a Web-TechServices Partner',
    subheadline: 'Earn commissions on every referral',
    description: 'Join our Partner Program. Earn commissions every time someone you refer signs up. No tracking-link gymnastics — just warm intros. We\'ll send the application straight to your inbox.',
    cta: 'Start My Partner Application',
    redirect: '/affiliate-landing',
    icon: DollarSign,
    gradient: 'from-blue-600 to-indigo-700',
    bgGlow: 'bg-blue-500/20',
    accent: 'text-blue-300',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
];

// Pages where popup should NOT show
const EXCLUDED_PATHS = [
  '/admin', '/login', '/ai-visibility-trial',
  '/warm-handoffs',
  '/partner/hq', '/partner/install',
  '/affiliate/dashboard',
  '/hosting', '/client-portal', '/client', '/ppc-portal', '/client-pay',
];

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

// Deterministic variant pick based on page path — each page always shows the same offer
function getVariantForPath(path) {
  let hash = 0;
  for (let i = 0; i < path.length; i++) {
    hash = ((hash << 5) - hash) + path.charCodeAt(i);
    hash |= 0;
  }
  return VARIANTS[Math.abs(hash) % VARIANTS.length];
}

export default function ExitIntentPopup() {
  const { i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // Honeypot field
  const [formLoadedAt, setFormLoadedAt] = useState(null); // Timing protection
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState('');

  // Check if popup already shown this browser session (survives SPA navigation)
  const alreadyShownThisSession = useCallback(() => {
    return sessionStorage.getItem('popup_shown') === '1';
  }, []);

  const triggerPopup = useCallback(() => {
    // Don't show on excluded pages
    if (EXCLUDED_PATHS.some(p => window.location.pathname.startsWith(p))) return;
    // Don't show if dismissed (cookie-based, persists across sessions for 7 days)
    if (getCookie('popup_dismissed')) return;
    // Don't show if already shown THIS browser session (one popup per visit)
    if (alreadyShownThisSession()) return;

    // Pick variant deterministically based on current page path
    const v = getVariantForPath(window.location.pathname);
    setVariant(v);
    setShow(true);
    setFormLoadedAt(Date.now());
    // Mark as shown for the entire browser session
    sessionStorage.setItem('popup_shown', '1');
    setCookie('popup_last_variant', v.id, 30);
  }, [alreadyShownThisSession]);

  useEffect(() => {
    // Skip if already shown this session
    if (alreadyShownThisSession()) return;

    // Exit intent detection (desktop)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 5 && !show) {
        triggerPopup();
      }
    };

    // Timer-based trigger (45 seconds — single popup policy)
    const timer = setTimeout(() => {
      if (!show && !getCookie('popup_dismissed') && !alreadyShownThisSession()) {
        triggerPopup();
      }
    }, 45000);

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timer);
    };
  }, [show, triggerPopup, alreadyShownThisSession]);

  const handleDismiss = () => {
    setShow(false);
    setCookie('popup_dismissed', '1', 7); // Don't show for 7 days
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    const emailErr = validateEmail(email);
    if (emailErr) { setMessage(emailErr); return; }
    const cleanEmail = normalizeEmail(email.trim());
    setSubmitting(true);

    try {
      const res = await fetch(`${API}/api/popup/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: cleanEmail,
          variant_id: variant.id,
          page_url: window.location.pathname,
          preferred_language: (i18n.language || 'en').split('-')[0] === 'es' ? 'es' : 'en',
          // Spam protection fields
          website: website, // Honeypot - should be empty
          form_loaded_at: formLoadedAt, // Timing check
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setMessage(data.message);
        setCookie('popup_dismissed', '1', 30); // Don't show for 30 days after submit
        // Redirect after a short delay
        setTimeout(() => {
          setShow(false);
          if (data.redirect) {
            window.location.href = data.redirect;
          }
        }, 2500);
      }
    } catch (err) {
      console.error('Popup submit error:', err);
    }
    setSubmitting(false);
  };

  if (!show || !variant) return null;

  const IconComponent = variant.icon;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      data-testid="exit-intent-popup"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleDismiss}
      />

      {/* Popup Card */}
      <div
        className="relative w-full max-w-md bg-slate-900 rounded-2xl shadow-2xl border border-white/10 overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        data-testid={`popup-variant-${variant.id}`}
      >
        {/* Top gradient bar */}
        <div className={`h-1.5 bg-gradient-to-r ${variant.gradient}`} />

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors"
          data-testid="popup-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 pt-5">
          {!submitted ? (
            <>
              {/* Icon + Badge */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${variant.bgGlow}`}>
                  <IconComponent className={`w-6 h-6 ${variant.accent}`} />
                </div>
                <div className="flex items-center gap-1.5 text-xs text-white/60">
                  <Sparkles className="w-3 h-3" />
                  <span>Limited Time Offer</span>
                </div>
              </div>

              {/* Headline */}
              <h3 className="text-2xl font-bold text-white mb-1" data-testid="popup-headline">
                {variant.headline}
              </h3>
              <p className={`text-sm font-medium ${variant.accent} mb-3`}>
                {variant.subheadline}
              </p>
              <p className="text-sm text-white/70 mb-5 leading-relaxed">
                {variant.description}
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="popup-form">
                {/* Honeypot field - hidden from users, bots will fill it */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm focus:outline-none focus:border-white/40 focus:ring-1 focus:ring-white/20"
                  required
                  data-testid="popup-name-input"
                />
                <input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm focus:outline-none focus:border-white/40 focus:ring-1 focus:ring-white/20"
                  required
                  data-testid="popup-email-input"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full py-3 rounded-lg text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all ${variant.btnClass} disabled:opacity-50`}
                  data-testid="popup-submit-btn"
                >
                  {submitting ? 'Submitting...' : (
                    <>
                      {variant.cta}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              <p className="text-xs text-white/30 mt-3 text-center">
                No spam, ever. Unsubscribe anytime.
              </p>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-6" data-testid="popup-success">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${variant.bgGlow} flex items-center justify-center`}>
                <Sparkles className={`w-8 h-8 ${variant.accent}`} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">You're In!</h3>
              <p className="text-white/70 text-sm">{message}</p>
              <p className="text-xs text-white/40 mt-3">Redirecting you now...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
